import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Loader2, Info, CalendarDays, RefreshCcw,
    ChevronLeft, Printer, FileSpreadsheet, Download,
    Scale, AlertCircle, CheckCircle2,
    Store
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { useToast } from '../hooks/useToast';
import { fetchBalanceSheet, currencyCodeToSymbolMap, fetchStoresWhereStaffAssigned } from '../apiHelper';

// --- STYLING HELPERS ---
const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const formatCurrency = (amount, currencyCode) => {
    const symbol = currencyCodeToSymbolMap[currencyCode] || '₦';
    return `${symbol}${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Math.abs(amount))}`;
};

const NewBalanceSheetReport = ({ onBack }) => {
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // As of date parameters mapping to trial balance payload expectations
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 365);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState('');

    const initializeStores = async (parsedUser) => {
        try {
            const fetchedStores = await fetchStoresWhereStaffAssigned(parsedUser);
            const options = fetchedStores
                .filter(store => store.storetype !== 'WHS')
                .map(store => ({
                    label: store.identifier,
                    value: store.store_id,
                }));
            setStores(options);
        } catch (err) {
            console.error(err);
            showToast(err.response?.data?.detail || "Failed to load assigned stores", "error");
        }
    };

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            initializeStores(parsedUser);
        } else {
            showToast('User not found. Please log in.', 'error');
            navigate('/login');
        }
    }, [navigate, showToast]);

    const loadBalanceSheet = async () => {
        if (!user) return;
        setLoading(true);
        setError(null);
        try {
            const params = {
                users_id: user.users_id,
                start_date: startDate,
                end_date: endDate,
                store_id: selectedStoreId || null,
            };
            const response = await fetchBalanceSheet(params);
            console.log(response);
            setReportData(response);
        } catch (err) {
            setError(err.message || 'Failed to load balance sheet.');
            showToast('Error loading report', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) loadBalanceSheet();
    }, [user, startDate, endDate, selectedStoreId]);

    const currencyCode = reportData?.currency_code || 'NGN';
    const isBalanced = reportData?.is_balanced ?? true;

    // --- EXCEL EXPORT FUNCTION ---
    const exportToExcel = () => {
        if (!reportData) {
            showToast('No data available to export', 'error');
            return;
        }

        const excelData = [];

        // Helper to push sections
        const addSectionToExcel = (categoryName, sections) => {
            excelData.push({ 'Account Code': `--- ${categoryName} ---`, 'Account Name': '', 'Type': '', 'Balance': '' });
            sections?.forEach(sec => {
                excelData.push({ 'Account Code': `Section: ${sec.section_name}`, 'Account Name': '', 'Type': '', 'Balance': sec.section_total });
                sec.accounts.forEach(acc => {
                    excelData.push({
                        'Account Code': acc.account_code || '',
                        'Account Name': acc.account_name || '',
                        'Type': acc.account_type || '',
                        'Balance': acc.ending_balance || 0
                    });
                });
            });
        };

        addSectionToExcel('ASSETS', reportData.assets);
        addSectionToExcel('LIABILITIES', reportData.liabilities);
        addSectionToExcel('EQUITY', reportData.equity);

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        worksheet['!cols'] = [{ wch: 15 }, { wch: 45 }, { wch: 20 }, { wch: 18 }];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Balance Sheet');
        XLSX.writeFile(workbook, `Balance_Sheet_${startDate}_to_${endDate}.xlsx`);
    };

    // --- PDF EXPORT FUNCTION ---
    const exportToPDF = () => {
        if (!reportData) {
            showToast('No data available to export', 'error');
            return;
        }

        const doc = new jsPDF('p', 'pt', 'a4');

        doc.setFontSize(18);
        doc.setTextColor(30, 41, 59);
        doc.text(reportData.organization_name || 'Balance Sheet Report', 40, 40);

        doc.setFontSize(12);
        doc.setTextColor(100, 116, 139);
        doc.text(`Balance Sheet as of ${reportData.as_of_date || endDate}`, 40, 58);

        doc.setFontSize(9);
        doc.text(`Status: ${isBalanced ? 'Balanced' : 'Out of Balance'}`, 40, 72);

        const tableRows = [];
        const pushSectionsToPdf = (title, sections) => {
            tableRows.push([{ content: title, colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }]);
            sections?.forEach(sec => {
                tableRows.push([`  ${sec.section_name}`, formatCurrency(sec.section_total, currencyCode)]);
                sec.accounts.forEach(acc => {
                    tableRows.push([`    - ${acc.account_name} (${acc.account_code})`, formatCurrency(acc.ending_balance, currencyCode)]);
                });
            });
        };

        pushSectionsToPdf('ASSETS', reportData.assets);
        tableRows.push(['Total Assets', formatCurrency(reportData.total_assets, currencyCode)]);

        pushSectionsToPdf('LIABILITIES', reportData.liabilities);
        tableRows.push(['Total Liabilities', formatCurrency(reportData.total_liabilities, currencyCode)]);

        pushSectionsToPdf('EQUITY', reportData.equity);
        tableRows.push(['Total Equity', formatCurrency(reportData.total_equity, currencyCode)]);

        autoTable(doc, {
            startY: 85,
            head: [['Account Details', 'Ending Balance']],
            body: tableRows,
            theme: 'grid',
            headStyles: {
                fillColor: [249, 250, 251],
                textColor: [107, 114, 128],
                fontSize: 9,
                fontStyle: 'bold'
            },
            bodyStyles: {
                fontSize: 9,
                textColor: [17, 24, 39]
            },
            columnStyles: {
                0: { cellWidth: 'auto' },
                1: { halign: 'right', cellWidth: 150 }
            }
        });

        doc.save(`Balance_Sheet_${startDate}_to_${endDate}.pdf`);
    };

    return (
        <div className="min-h-screen bg-[#F9FAFB] pb-12">
            <nav className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-zinc-200 px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <button
                        onClick={onBack || (() => navigate(-1))}
                        className="flex items-center text-zinc-500 hover:text-zinc-900 transition-colors font-medium text-sm gap-2"
                    >
                        <ChevronLeft size={18} /> Back to Reports
                    </button>
                    <div className="flex items-center gap-3">
                        <Button
                            variant="secondary"
                            onClick={exportToExcel}
                            className="flex items-center justify-center bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-700 shadow-sm px-4"
                        >
                            <FileSpreadsheet size={16} className="mr-2 text-emerald-600" /> Export Excel
                        </Button>

                        <Button
                            variant="primary"
                            onClick={exportToPDF}
                            className="flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 shadow-sm px-4"
                        >
                            <Download size={16} className="mr-2" /> Export PDF
                        </Button>
                    </div>
                </div>
            </nav>

            <main className="max-w-7xl mx-auto px-6 mt-8 space-y-6">
                <div className="bg-white rounded-2xl border border-zinc-200 p-8 shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2 text-indigo-600 font-semibold text-xs uppercase tracking-widest">
                                <Scale size={14} /> Financial Statement
                            </div>
                            <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">Balance Sheet</h1>
                            <p className="text-zinc-500 font-medium">{reportData?.organization_name || 'All Organizations'}</p>
                        </div>

                        <div className={`flex items-center gap-3 px-4 py-2 rounded-xl border ${isBalanced
                            ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
                            : 'bg-amber-50 border-amber-100 text-amber-700'
                            }`}>
                            {isBalanced ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                            <div className="text-sm">
                                <p className="font-bold leading-none">{isBalanced ? 'Balanced' : 'Out of Balance'}</p>
                                <p className="text-[11px] opacity-80">System Integrity Check</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-6 mt-8 pt-8 border-t border-zinc-100">
                        <div className="flex items-center gap-2 bg-zinc-50 p-2 rounded-xl border border-zinc-100 min-w-[200px]">
                            <Store size={16} className="text-zinc-400 ml-2" />
                            <select
                                value={selectedStoreId}
                                onChange={(e) => setSelectedStoreId(e.target.value)}
                                className="bg-transparent text-sm font-semibold text-zinc-700 focus:outline-none border-none w-full cursor-pointer"
                            >
                                <option value="">All Branches</option>
                                {stores.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-3 bg-zinc-50 p-2 rounded-xl border border-zinc-100">
                            <div className="flex items-center gap-2">
                                <label htmlFor="startDate" className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider px-2">From</label>
                                <input
                                    type="date"
                                    id="startDate"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="bg-transparent text-sm font-semibold text-zinc-700 focus:outline-none border-none p-0"
                                />
                            </div>
                            <div className="h-4 w-[1px] bg-zinc-200" />
                            <div className="flex items-center gap-2">
                                <label htmlFor="endDate" className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider px-2">To</label>
                                <input
                                    type="date"
                                    id="endDate"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="bg-transparent text-sm font-semibold text-zinc-700 focus:outline-none border-none p-0"
                                />
                            </div>
                        </div>

                        <button
                            onClick={loadBalanceSheet}
                            className="ml-auto flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-colors"
                            title="Refresh Report"
                        >
                            <RefreshCcw size={16} className={loading ? 'animate-spin' : ''} />
                            <span className="text-xs font-bold uppercase">Update Report</span>
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-zinc-50/50 border-b border-zinc-200">
                                    <th className="px-8 py-4 text-[11px] font-bold text-zinc-500 uppercase tracking-widest">Account Details</th>
                                    <th className="px-6 py-4 text-[11px] font-bold text-zinc-500 uppercase tracking-widest">Type</th>
                                    <th className="px-6 py-4 text-[11px] font-bold text-zinc-500 uppercase tracking-widest text-right">Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan="3" className="px-8 py-20">
                                            <div className="flex flex-col items-center justify-center gap-3">
                                                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                                                <p className="text-zinc-400 font-medium">Calculating balance sheet...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : !reportData ? (
                                    <tr>
                                        <td colSpan="3" className="px-8 py-20 text-center text-zinc-400 italic">
                                            No balance sheet data found for this period.
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {/* Render Assets */}
                                        <tr className="bg-zinc-100/70"><td colSpan="3" className="px-8 py-3 font-bold text-xs uppercase text-zinc-600 tracking-wider">Assets</td></tr>
                                        {reportData.assets?.map(sec => (
                                            <React.Fragment key={sec.section_key}>
                                                <tr className="bg-zinc-50/40"><td colSpan="3" className="px-10 py-2 font-semibold text-xs text-zinc-500">{sec.section_name} — Total: {formatCurrency(sec.section_total, currencyCode)}</td></tr>
                                                {sec.accounts.map(acc => (
                                                    <motion.tr
                                                        key={acc.account_id}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        onClick={() => navigate('/reports/account-ledger-report', { state: { account_id: acc.account_id } })}
                                                        className="cursor-pointer hover:bg-zinc-50 transition-colors group"
                                                    >
                                                        <td className="px-12 py-3">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-bold text-zinc-800 group-hover:text-indigo-600 transition-colors">{acc.account_name}</span>
                                                                <span className="text-xs font-mono text-zinc-400">{acc.account_code}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3">
                                                            <span className="px-2.5 py-1 rounded-md bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-wider">{acc.account_type}</span>
                                                        </td>
                                                        {/* <td className="px-6 py-3 text-sm font-mono text-right text-zinc-700">{formatCurrency(acc.ending_balance, currencyCode)}</td> */}
                                                        {(() => {
                                                            const bal = acc.ending_balance || 0;
                                                            const isNegAsset = acc.account_type_code === 'asset' && bal < 0;
                                                            return (
                                                                <td className={`px-6 py-3 text-sm font-mono text-right ${isNegAsset ? 'text-amber-600' : 'text-zinc-700'}`}>
                                                                    {formatCurrency(bal, currencyCode)}
                                                                    {isNegAsset && <span className="ml-1 text-[10px] font-bold">CR</span>}
                                                                </td>
                                                            );
                                                        })()}
                                                    </motion.tr>
                                                ))}
                                            </React.Fragment>
                                        ))}

                                        {/* Render Liabilities */}
                                        <tr className="bg-zinc-100/70"><td colSpan="3" className="px-8 py-3 font-bold text-xs uppercase text-zinc-600 tracking-wider">Liabilities</td></tr>
                                        {reportData.liabilities?.map(sec => (
                                            <React.Fragment key={sec.section_key}>
                                                <tr className="bg-zinc-50/40"><td colSpan="3" className="px-10 py-2 font-semibold text-xs text-zinc-500">{sec.section_name} — Total: {formatCurrency(sec.section_total, currencyCode)}</td></tr>
                                                {sec.accounts.map(acc => (
                                                    <motion.tr
                                                        key={acc.account_id}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        onClick={() => navigate('/reports/account-ledger-report', { state: { account_id: acc.account_id } })}
                                                        className="cursor-pointer hover:bg-zinc-50 transition-colors group"
                                                    >
                                                        <td className="px-12 py-3">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-bold text-zinc-800 group-hover:text-indigo-600 transition-colors">{acc.account_name}</span>
                                                                <span className="text-xs font-mono text-zinc-400">{acc.account_code}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3">
                                                            <span className="px-2.5 py-1 rounded-md bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-wider">{acc.account_type}</span>
                                                        </td>
                                                        {/* <td className="px-6 py-3 text-sm font-mono text-right text-zinc-700">{formatCurrency(acc.ending_balance, currencyCode)}</td> */}
                                                        {(() => {
                                                            const bal = acc.ending_balance || 0;
                                                            const isNegAsset = acc.account_type_code === 'asset' && bal < 0;
                                                            return (
                                                                <td className={`px-6 py-3 text-sm font-mono text-right ${isNegAsset ? 'text-amber-600' : 'text-zinc-700'}`}>
                                                                    {formatCurrency(bal, currencyCode)}
                                                                    {isNegAsset && <span className="ml-1 text-[10px] font-bold">CR</span>}
                                                                </td>
                                                            );
                                                        })()}
                                                    </motion.tr>
                                                ))}
                                            </React.Fragment>
                                        ))}

                                        {/* Render Equity */}
                                        <tr className="bg-zinc-100/70"><td colSpan="3" className="px-8 py-3 font-bold text-xs uppercase text-zinc-600 tracking-wider">Equity</td></tr>
                                        {reportData.equity?.map(sec => (
                                            <React.Fragment key={sec.section_key}>
                                                <tr className="bg-zinc-50/40"><td colSpan="3" className="px-10 py-2 font-semibold text-xs text-zinc-500">{sec.section_name} — Total: {formatCurrency(sec.section_total, currencyCode)}</td></tr>
                                                {sec.accounts.map(acc => (
                                                    <motion.tr
                                                        key={acc.account_id}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        onClick={() => navigate('/reports/account-ledger-report', { state: { account_id: acc.account_id } })}
                                                        className="cursor-pointer hover:bg-zinc-50 transition-colors group"
                                                    >
                                                        <td className="px-12 py-3">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm font-bold text-zinc-800 group-hover:text-indigo-600 transition-colors">{acc.account_name}</span>
                                                                <span className="text-xs font-mono text-zinc-400">{acc.account_code}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-3">
                                                            <span className="px-2.5 py-1 rounded-md bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-wider">{acc.account_type}</span>
                                                        </td>
                                                        {/* <td className="px-6 py-3 text-sm font-mono text-right text-zinc-700">{formatCurrency(acc.ending_balance, currencyCode)}</td> */}
                                                        {(() => {
                                                            const bal = acc.ending_balance || 0;
                                                            const isNegAsset = acc.account_type_code === 'asset' && bal < 0;
                                                            return (
                                                                <td className={`px-6 py-3 text-sm font-mono text-right ${isNegAsset ? 'text-amber-600' : 'text-zinc-700'}`}>
                                                                    {formatCurrency(bal, currencyCode)}
                                                                    {isNegAsset && <span className="ml-1 text-[10px] font-bold">CR</span>}
                                                                </td>
                                                            );
                                                        })()}
                                                    </motion.tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </>
                                )}
                            </tbody>
                            {reportData && (
                                <tfoot className="bg-zinc-900 text-white">
                                    <tr>
                                        <td colSpan="2" className="px-8 py-4 text-sm font-bold uppercase tracking-widest text-zinc-400">Total Assets</td>
                                        <td className="px-6 py-4 text-lg font-mono text-right font-bold">{formatCurrency(reportData.total_assets, currencyCode)}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan="2" className="px-8 py-4 text-sm font-bold uppercase tracking-widest text-zinc-400">Total Liabilities + Equity</td>
                                        <td className="px-6 py-4 text-lg font-mono text-right font-bold">{formatCurrency(reportData.total_liabilities + reportData.total_equity, currencyCode)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default NewBalanceSheetReport;